import { WorkbenchLink } from '@affine/core/modules/workbench';
import { ArrowDownSmallIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import React, { type SVGAttributes } from 'react';
import type { To } from 'react-router-dom';

import * as styles from './index.css';

export interface MenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactElement<SVGAttributes<SVGElement>>;
  active?: boolean;
  disabled?: boolean;
  // true, false, undefined. undefined means no collapse
  collapsed?: boolean;
  // if onCollapsedChange is given, but collapsed is undefined, then we will render the collapse button as disabled
  onCollapsedChange?: (collapsed: boolean) => void;
  postfix?: React.ReactElement;
  postfixDisplay?: 'always' | 'hover';
  /** Link wrapper owns keyboard and link semantics. */
  linkChild?: boolean;
}

export interface MenuLinkItemProps extends MenuItemProps {
  to: To;
  linkComponent?: React.ComponentType<
    React.PropsWithChildren<{
      className?: string;
      draggable?: boolean;
      to: To;
    }>
  >;
}

const stopPropagation: React.MouseEventHandler = e => {
  e.stopPropagation();
};

const HOVER_SUPPRESSION_RESET_DISTANCE = 2;

/**
 * This component is not a generic component.
 * It is used for the app sidebar.
 */
export const MenuItem = React.forwardRef<HTMLDivElement, MenuItemProps>(
  (
    {
      onClick,
      onKeyDown,
      onPointerLeave,
      onPointerMove,
      icon,
      active,
      children,
      disabled,
      collapsed,
      onCollapsedChange,
      postfix,
      postfixDisplay = 'hover',
      linkChild = false,
      ...props
    },
    ref
  ) => {
    const collapsible = onCollapsedChange !== undefined;
    const [postfixHoverSuppressed, setPostfixHoverSuppressed] =
      React.useState(false);
    const hoverSuppressionOriginRef = React.useRef<{
      x: number;
      y: number;
    } | null>(null);

    const clearPostfixHoverSuppression = React.useCallback(() => {
      hoverSuppressionOriginRef.current = null;
      setPostfixHoverSuppressed(false);
    }, []);

    const handlePointerMove = React.useCallback(
      (event: React.PointerEvent<HTMLDivElement>) => {
        onPointerMove?.(event);
        if (!postfixHoverSuppressed) {
          return;
        }

        const origin = hoverSuppressionOriginRef.current;
        if (
          !origin ||
          Math.abs(event.clientX - origin.x) >=
            HOVER_SUPPRESSION_RESET_DISTANCE ||
          Math.abs(event.clientY - origin.y) >= HOVER_SUPPRESSION_RESET_DISTANCE
        ) {
          clearPostfixHoverSuppression();
        }
      },
      [clearPostfixHoverSuppression, onPointerMove, postfixHoverSuppressed]
    );

    const handlePointerLeave = React.useCallback(
      (event: React.PointerEvent<HTMLDivElement>) => {
        onPointerLeave?.(event);
        clearPostfixHoverSuppression();
      },
      [clearPostfixHoverSuppression, onPointerLeave]
    );

    const handlePostfixClickCapture = React.useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        hoverSuppressionOriginRef.current = {
          x: event.clientX,
          y: event.clientY,
        };
        setPostfixHoverSuppressed(true);
      },
      []
    );

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        onKeyDown?.(event);
        if (
          event.defaultPrevented ||
          disabled ||
          linkChild ||
          !onClick ||
          (event.key !== 'Enter' && event.key !== ' ')
        ) {
          return;
        }
        event.preventDefault();
        event.currentTarget.click();
      },
      [disabled, linkChild, onClick, onKeyDown]
    );

    const keyboardInteractive = Boolean(onClick) && !disabled && !linkChild;

    return (
      <div
        ref={ref}
        {...props}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        onPointerLeave={handlePointerLeave}
        onPointerMove={handlePointerMove}
        className={clsx([styles.root, props.className])}
        data-active={active}
        data-disabled={disabled}
        data-collapsible={collapsible}
        data-hover-suppressed={postfixHoverSuppressed}
        role={props.role ?? (onClick && !linkChild ? 'button' : undefined)}
        tabIndex={props.tabIndex ?? (keyboardInteractive ? 0 : undefined)}
        aria-disabled={disabled || undefined}
      >
        {icon && (
          <div className={styles.iconsContainer} data-collapsible={collapsible}>
            {collapsible && (
              <div
                data-disabled={collapsed === undefined ? true : undefined}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault(); // for links
                  onCollapsedChange?.(!collapsed);
                }}
                data-testid="fav-collapsed-button"
                className={styles.collapsedIconContainer}
              >
                <ArrowDownSmallIcon
                  className={styles.collapsedIcon}
                  data-collapsed={collapsed !== false}
                />
              </div>
            )}
            {React.cloneElement(icon, {
              className: clsx([styles.icon, icon.props.className]),
            })}
          </div>
        )}

        <div className={styles.content}>{children}</div>
        {postfix ? (
          <div
            className={styles.postfix}
            data-postfix-display={postfixDisplay}
            data-sidebar-action-reveal-fade
            onClick={stopPropagation}
            onClickCapture={handlePostfixClickCapture}
          >
            {postfix}
          </div>
        ) : null}
      </div>
    );
  }
);
MenuItem.displayName = 'MenuItem';

export const MenuLinkItem = React.forwardRef<HTMLDivElement, MenuLinkItemProps>(
  ({ to, linkComponent: LinkComponent = WorkbenchLink, ...props }, ref) => {
    return (
      <LinkComponent to={to} className={styles.linkItemRoot} draggable={false}>
        {/* The <a> element rendered by Link does not generate display box due to `display: contents` style */}
        {/* Thus ref is passed to MenuItem instead of Link */}
        <MenuItem ref={ref} linkChild {...props}></MenuItem>
      </LinkComponent>
    );
  }
);
MenuLinkItem.displayName = 'MenuLinkItem';
